/**
 * chess.js ↔ chessground helpers for the new in-house wrapper.
 * Keep these pure (no React, no DOM) so they're trivial to test.
 */
import {Chess, validateFen} from 'chess.js';

// chessground 9's package.json has `"type": "module"` without an `exports`
// field; some TS resolution modes can't find subpath .d.ts files. Inline the
// types we use — they're tiny and stable across chessground versions.
type Key = string;
type Dests = Map<Key, Key[]>;

export type ChessgroundColor = 'white' | 'black';

export type ThemeName =
  | 'green-white'
  | 'blue-white'
  // PlayChess V2 "Premium Themes" boards (themes.ts, M1a)
  | 'gallery'
  | 'gallery-dark'
  | null
  | undefined
  | string;

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;

export const STANDARD_START_FEN =
  'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

export function safeChess(
  fen?: string | null,
  fallbackFen: string = STANDARD_START_FEN,
): Chess {
  const candidate = fen && fen !== 'start' ? fen : fallbackFen;
  try {
    if (validateFen(candidate).ok) return new Chess(candidate);
    console.warn('[chess] invalid FEN, using fallback:', candidate);
  } catch {
    console.warn('[chess] FEN validation threw, using fallback:', candidate);
  }
  try {
    return new Chess(fallbackFen);
  } catch {
    return new Chess();
  }
}

/**
 * Build chessground's `dests` Map (origin square → legal target squares) from a
 * chess.js instance. If `restrictedSquare` is set, only that square's legal
 * moves are returned (PlayChess uses this in puzzle mode where a single piece
 * is movable).
 */
export function buildDests(
  game: Chess,
  restrictedSquare?: string | null,
): Dests {
  const map: Dests = new Map();
  const moves = restrictedSquare
    ? game.moves({square: restrictedSquare as any, verbose: true})
    : game.moves({verbose: true});
  for (const m of moves as Array<{from: string; to: string}>) {
    const arr = map.get(m.from as Key);
    if (arr) arr.push(m.to as Key);
    else map.set(m.from as Key, [m.to as Key]);
  }
  return map;
}

export function turnColorOf(game: Chess): ChessgroundColor {
  return game.turn() === 'w' ? 'white' : 'black';
}

/**
 * PlayChessV2 M2 review fix (2026-10-06): with `movableColor: 'both'`
 * (self-analysis), `onUserMove`'s real-move/premove branch must follow the
 * REAL turn, not the fixed `boardOrientation` — otherwise every drop by
 * whichever side is NOT the board's orientation fails `realTurn ===
 * userColor` and is silently routed to `enqueuePremove` instead of
 * `commitLocalMove` (the only caller of `onPieceDropExtension`), so it
 * never reaches `analysisMove`. `undefined` / `'white'` / `'black'` are
 * byte-identical to today for every other caller: `userColor` stays
 * `boardOrientation`.
 */
export function movableUserColor(
  realTurn: ChessgroundColor,
  boardOrientation: ChessgroundColor,
  movableColor: 'white' | 'black' | 'both' | undefined,
): ChessgroundColor {
  return movableColor === 'both' ? realTurn : boardOrientation;
}

/**
 * True if dropping the piece currently on `from` onto `to` is a pawn promotion.
 * Must be called BEFORE chess.move() so the pawn is still on `from`.
 */
export function isPromotionMove(game: Chess, from: string, to: string): boolean {
  const piece = game.get(from as any);
  if (!piece || piece.type !== 'p') return false;
  const rank = to[1];
  return (
    (piece.color === 'w' && rank === '8') ||
    (piece.color === 'b' && rank === '1')
  );
}

/**
 * Returns the square the king-in-check is on, or undefined if not in check.
 * chessground takes a Color/boolean for `check`, but rendering the radial
 * highlight at a specific square also benefits us (the FEN diff happens fast).
 */
export function kingInCheckSquare(game: Chess): string | undefined {
  if (!game.inCheck()) return undefined;
  const turn = game.turn();
  const board = game.board();
  for (let r = 0; r < 8; r++) {
    for (let f = 0; f < 8; f++) {
      const p = board[r][f];
      if (p && p.type === 'k' && p.color === turn) {
        return FILES[f] + (8 - r);
      }
    }
  }
  return undefined;
}

/**
 * Replays legal moves from `prev` to find the one yielding `curr`, returning
 * [from, to]. Replaces `chessboard-package`'s `findMoveFromFens`. Only used
 * when an EXTERNAL position change arrives (opponent move from socket); for
 * our own moves we already have the {from, to} from events.after.
 *
 * O(legal_moves_at_prev) chess.move/undo cycles. Fine at ~one call per
 * opponent move; do NOT call inside the drag path.
 */
export function findMoveFromFens(
  prev: string,
  curr: string,
): [string, string] | null {
  try {
    const game = new Chess(prev);
    const moves = game.moves({verbose: true}) as Array<{
      from: string;
      to: string;
    }>;
    for (const m of moves) {
      game.move(m);
      if (game.fen() === curr) {
        game.undo();
        return [m.from, m.to];
      }
      game.undo();
    }
  } catch {
    /* fall through */
  }
  return null;
}

/**
 * Cheap FEN piece-count diff: was a piece captured between two adjacent FENs?
 * Used for capture-vs-move sound selection. Safer than reading the move object
 * because it works for externally-applied positions.
 */
export function didCaptureOccur(prev: string, curr: string): boolean {
  const count = (fen: string) =>
    fen.split(' ')[0].replace(/[\d/]/g, '').length;
  return count(curr) < count(prev);
}

/**
 * Returns a new Chess instance with the side-to-move marker flipped to
 * `color` (and en-passant cleared). Needed because `chess.js@v1` refuses
 * out-of-turn moves with an exception — but a queued premove represents the
 * user's intent to move during the opponent's turn (or in a chain after
 * a premove they already queued). We flip the FEN's turn marker just before
 * applying the premove so `chess.move` accepts it; the resulting position
 * is the "speculative" board we render. It is NOT a real chess position and
 * shouldn't be persisted anywhere except as the rendered/speculative state.
 */
export function flipTurnToMatch(chess: Chess, color: 'w' | 'b'): Chess {
  if (chess.turn() === color) return chess;
  const parts = chess.fen().split(' ');
  parts[1] = color;
  parts[3] = '-'; // en-passant target square is invalid after a turn flip
  try {
    return new Chess(parts.join(' '));
  } catch {
    return chess;
  }
}

// ---------- "loose" move application for speculative premoves ----------
// chessground's premove geometry accepts moves chess.js refuses (pawn diagonal
// to empty square, bishop through own piece, capture own piece, etc.). All
// of these can become legal LATER — e.g. after the opponent moves a piece
// into the pawn's diagonal target, or after a chained earlier premove moves
// own pieces out of the way. For the SPECULATIVE board we just want to
// render the result of moving the piece; chess.js strict validation only
// applies when we replay against the real position (isPremoveStillLegal).

function squareToCoords(sq: string): {row: number; col: number} {
  // Files a-h → col 0-7. Ranks 1-8 → row 7-0 (rank 8 is top = row 0 in FEN).
  return {
    col: sq.charCodeAt(0) - 'a'.charCodeAt(0),
    row: 8 - parseInt(sq[1], 10),
  };
}

function parseFenPieces(piecePlacement: string): Array<Array<string | null>> {
  const board: Array<Array<string | null>> = Array.from({length: 8}, () =>
    Array(8).fill(null),
  );
  const rows = piecePlacement.split('/');
  for (let r = 0; r < 8; r++) {
    let col = 0;
    for (const c of rows[r] || '') {
      if (/\d/.test(c)) col += parseInt(c, 10);
      else {
        board[r][col] = c;
        col++;
      }
    }
  }
  return board;
}

function boardToFenPieces(board: Array<Array<string | null>>): string {
  return board
    .map(row => {
      let out = '';
      let empty = 0;
      for (const c of row) {
        if (c === null) empty++;
        else {
          if (empty > 0) {
            out += empty;
            empty = 0;
          }
          out += c;
        }
      }
      if (empty > 0) out += empty;
      return out;
    })
    .join('/');
}

/**
 * Apply a move to a speculative chess.js instance, allowing moves chess.js's
 * strict legality check would refuse.
 *
 * Try chess.move() first (handles castling, en passant, promotion validation
 * correctly when the move IS legal). If it throws/rejects, fall back to
 * direct FEN piece manipulation — remove piece from source, place it at
 * destination, flip turn, reset en-passant. The resulting FEN may represent
 * a position that wouldn't arise in legal play; chess.js still has to be
 * able to load it for chessground's rendering to work. If chess.js refuses
 * to load even the rewritten FEN (e.g. position has no king), we give up
 * and return null — the caller should skip this premove.
 */
export function applyMoveLoose(
  spec: Chess,
  from: string,
  to: string,
  promotion?: string,
): Chess | null {
  // 1. Try strict chess.js move first (and on a clone so we don't mutate
  //    spec when it throws).
  try {
    const test = new Chess(spec.fen());
    const result = test.move({from, to, promotion: promotion || 'q'});
    if (result) return test;
  } catch {
    /* fall through to manual */
  }

  // 2. Manual fallback — geometric piece move with no rule validation.
  const fen = spec.fen();
  const parts = fen.split(' ');
  const board = parseFenPieces(parts[0]);

  const fromCo = squareToCoords(from);
  const toCo = squareToCoords(to);
  let piece = board[fromCo.row][fromCo.col];
  if (!piece) return null;

  if (promotion) {
    const isWhite = piece === piece.toUpperCase();
    piece = isWhite ? promotion.toUpperCase() : promotion.toLowerCase();
  }

  board[fromCo.row][fromCo.col] = null;
  board[toCo.row][toCo.col] = piece;

  const newPieces = boardToFenPieces(board);
  const newTurn = parts[1] === 'w' ? 'b' : 'w';
  // Reset en-passant; keep castling rights (we don't track them precisely on
  // spec — they only matter for the REAL chess.js when we replay).
  const newFen = `${newPieces} ${newTurn} ${parts[2] || '-'} - 0 ${parts[5] || '1'}`;

  try {
    return new Chess(newFen);
  } catch {
    // Resulting position is too broken for chess.js to load (e.g. captured
    // king). Bail; caller will skip this premove.
    return null;
  }
}

/**
 * A queued premove. Compared against the real position when the opponent's
 * move arrives, to decide whether to play this premove or cancel the chain.
 */
export type Premove = {
  from: string;
  to: string;
  promotion?: 'q' | 'r' | 'b' | 'n';
  /**
   * Piece code (e.g. "wn" = white knight) at the source square when this
   * premove was queued. If the opponent's move captures or otherwise removes
   * this piece, the premove is invalid and the chain breaks here.
   */
  pieceCodeAtQueue: string;
};

/**
 * Returns true if `premove` is still a legal move on `realGame` (the
 * server-confirmed position after the opponent's most recent move).
 *
 * "Still legal" requires:
 *   - the source square still has the SAME piece we queued (color + type), and
 *   - that piece has a legal move to the same destination square in the
 *     current real position.
 *
 * If false, the caller cancels this premove (and typically the rest of the
 * chain — chain-breaking is policy, see multi-premove-plan §2.6).
 */
export function isPremoveStillLegal(realGame: Chess, premove: Premove): boolean {
  try {
    const piece = realGame.get(premove.from as any);
    if (!piece) return false;
    if (piece.color + piece.type !== premove.pieceCodeAtQueue) return false;
    const moves = realGame.moves({
      square: premove.from as any,
      verbose: true,
    }) as Array<{from: string; to: string}>;
    return moves.some(m => m.to === premove.to);
  } catch {
    return false;
  }
}

export function themeClassName(theme: ThemeName): string {
  if (theme === 'green-white') return 'circlechess-theme-green-white';
  if (theme === 'blue-white') return 'circlechess-theme-blue-white';
  if (theme === 'gallery') return 'circlechess-theme-gallery';
  if (theme === 'gallery-dark') return 'circlechess-theme-gallery-dark';
  return 'circlechess-theme-default';
}

/**
 * Build chessground's `highlight.custom` Map for the last move, assigning
 * distinct classes to the from-square and to-square so each gets its own
 * theme color (cc-last-move-from / cc-last-move-to in themes.ts). Pair with
 * `highlight.lastMove: false` in the chessground config to suppress
 * chessground's single-class default.
 */
export function lastMoveHighlight(
  lastMove: [string, string] | undefined,
): Map<Key, string> | undefined {
  if (!lastMove) return undefined;
  return new Map<Key, string>([
    [lastMove[0] as Key, 'cc-last-move-from'],
    [lastMove[1] as Key, 'cc-last-move-to'],
  ]);
}
